import type {
  CostValueType,
  ProfileWritePayload,
  RawCost,
  RawProfile,
} from 'lumen/utils/net-revenue/cost/types';

/**
 * MOCK backend for cost profiles until the real API exists. It takes the same
 * payload the real endpoint would, so the serializer and transform still run.
 *
 * To go live:
 * 1. Flip `USE_MOCK` to `false` and confirm the endpoint in
 *    `app/adapters/cost-setting/profile.ts`.
 * 2. Delete the `if (USE_MOCK)` blocks there. `createRecord` can then go
 *    entirely; `query` and `updateRecord` must stay.
 * 3. Move the seeding helpers below to Polly or `tests/helpers/mock/`. The
 *    acceptance suite imports them, so this file cannot just be deleted.
 *
 * State lasts for the browser session. Tests reset it for determinism.
 */

/** See the file header to go live. */
export const USE_MOCK = true;

/** Exposed so tests can build a `RawProfile` directly, e.g. to seed a single
 * unambiguous row for row-action tests. */
export function simpleCommission(
  ...costs: { id: string; value: number; valueType?: CostValueType }[]
): RawProfile['costs'] {
  return costs.map(({ id, value, valueType = 'percentage' }) => ({
    id,
    commission_type: 'simple',
    cost_type: 'commission',
    value_type: valueType,
    cost_values: [
      { leadtime_end: null, leadtime_start: null, value, weekday: null },
    ],
  }));
}

/** Exposed so tests can seed an advanced (per-weekday) commission. */
export function advancedCommission(
  id: string,
  values: { value: number; weekday: number }[],
  valueType: CostValueType = 'percentage',
): RawProfile['costs'] {
  return [
    {
      id,
      commission_type: 'advanced',
      cost_type: 'commission',
      value_type: valueType,
      cost_values: values.map(({ value, weekday }) => ({
        leadtime_end: null,
        leadtime_start: null,
        value,
        weekday,
      })),
    },
  ];
}

function defaultProfiles(): RawProfile[] {
  return [
    {
      id: '1',
      name: 'OTA Standard Commission',
      description: 'Default commission applied to OTA channels.',
      created_at: '2026-01-15T10:00:00Z',
      updated_at: '2026-02-02T09:30:00Z',
      costs: simpleCommission({ id: 'c1', value: 15 }),
    },
    {
      id: '2',
      name: 'Direct Booking',
      description: 'No commission on direct reservations.',
      created_at: '2026-01-20T14:00:00Z',
      updated_at: '2026-01-20T14:00:00Z',
      costs: simpleCommission({ id: 'c2', value: 0 }),
    },
    {
      id: '3',
      name: 'Wholesale',
      description: 'Higher commission for wholesale partners.',
      created_at: '2026-02-10T08:15:00Z',
      updated_at: '2026-03-01T11:45:00Z',
      costs: simpleCommission({ id: 'c3', value: 22 }),
    },
    {
      id: '4',
      name: 'GDS Commission',
      description: 'Flat per-room-night fee for GDS-sourced bookings.',
      created_at: '2026-03-05T16:20:00Z',
      updated_at: '2026-03-05T16:20:00Z',
      costs: simpleCommission({ id: 'c4', value: 18, valueType: 'currency' }),
    },
    {
      id: '5',
      name: 'Unconfigured Profile',
      description: null,
      created_at: '2026-03-12T12:00:00Z',
      updated_at: '2026-03-12T12:00:00Z',
      costs: [],
    },
    {
      id: '6',
      name: 'Weekend Premium Commission',
      description: 'Higher commission on weekend bookings.',
      created_at: '2026-04-01T09:00:00Z',
      updated_at: '2026-04-01T09:00:00Z',
      costs: advancedCommission('c6', [
        { value: 15, weekday: 0 },
        { value: 15, weekday: 1 },
        { value: 15, weekday: 2 },
        { value: 15, weekday: 3 },
        { value: 15, weekday: 4 },
        { value: 25, weekday: 5 },
        { value: 25, weekday: 6 },
      ]),
    },
    {
      id: '7',
      name: 'GDS Peak Season Fee',
      description: 'Per-room-night fee that steps up towards the weekend.',
      created_at: '2026-04-10T09:00:00Z',
      updated_at: '2026-04-10T09:00:00Z',
      costs: advancedCommission(
        'c7',
        [
          { value: 10, weekday: 0 },
          { value: 10, weekday: 1 },
          { value: 10, weekday: 2 },
          { value: 10, weekday: 3 },
          { value: 20, weekday: 4 },
          { value: 20, weekday: 5 },
          { value: 20, weekday: 6 },
        ],
        'currency',
      ),
    },
  ];
}

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

let profiles: RawProfile[] = defaultProfiles();
let nextId = 1000;

export function resetMockProfiles(): void {
  profiles = defaultProfiles();
  nextId = 1000;
}

export function seedMockProfiles(seed: RawProfile[]): void {
  profiles = deepClone(seed);
  nextId = 1000;
}

export function mockCostProfiles(): { profiles: RawProfile[] } {
  return { profiles: deepClone(profiles) };
}

/** Stands in for the server assigning ids to new costs. */
function withCostIds(costs: RawCost[], profileId: string): RawCost[] {
  return costs.map((cost, index) => ({
    ...cost,
    id: cost.id ?? `${profileId}-c${index}`,
  }));
}

/** Returns the created record, matching the real POST response shape. */
export function addMockProfile(payload: ProfileWritePayload): RawProfile {
  const now = new Date().toISOString();
  const id = String(nextId++);
  const created: RawProfile = {
    ...deepClone(payload),
    id,
    created_at: now,
    updated_at: now,
  };
  created.costs = withCostIds(created.costs, id);
  profiles.push(created);

  return deepClone(created);
}

/** Merges rather than replaces, since a PATCH only carries changed keys. */
export function updateMockProfile(
  id: string,
  payload: Partial<ProfileWritePayload>,
): RawProfile {
  const index = profiles.findIndex((candidate) => candidate.id === id);
  if (index === -1) {
    // Like a real 404.
    throw new Error(`No mock cost profile found with id ${id}`);
  }

  const existing = profiles[index]!;
  const updated: RawProfile = {
    ...existing,
    ...deepClone(payload),
    id,
    created_at: existing.created_at,
    updated_at: new Date().toISOString(),
  };
  updated.costs = withCostIds(updated.costs, id);
  profiles[index] = updated;

  return deepClone(updated);
}

/** A missing id is a no-op here, matching a real DELETE's idempotent semantics. */
export function deleteMockProfile(id: string): void {
  profiles = profiles.filter((candidate) => candidate.id !== id);
}
